/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect } from 'react';
import {
  Container,
  Typography,
  Breadcrumbs,
  Box,
  LinearProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { useNavigationListener } from '../../components/hooks/useNavigationListener';
import { WritingInstructionEditor } from './InstructionEditor';
import { fetchWritingInstruction } from '@ink-ai/portal/reducers/writing-instruction';
import { RootState } from '@ink-ai/portal/reducers';

interface ImportReferenceStoreProps {
  referenceStoreUuid?: string;
  referenceStoreName?: string;
  referenceStoreDescription?: string;
}

export const EditWritingInstruction: React.FC<
  ImportReferenceStoreProps
> = () => {
  const instructionState = useSelector(
    (state: RootState) => state.writingInstruction,
  );
  const dispatch = useDispatch<any>();
  const { id } = useParams();
  useNavigationListener();

  const getInstructionById = async () => {
    try {
      dispatch(fetchWritingInstruction(id ?? ''));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getInstructionById();
  }, []);

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 1 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link to="/">Home</Link>
        <Link to="/writing-instruction">Writing Instruction</Link>
        <Typography color="textPrimary">Edit</Typography>
      </Breadcrumbs>
      {instructionState.loading ? (
        <Box sx={{ width: '100%' }}>
          <LinearProgress />
        </Box>
      ) : (
        <WritingInstructionEditor />
      )}
    </Container>
  );
};
